import { axiosInstance } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";
import {
  DeliveryConfig,
  ShippingRate,
  ShippingTypeOption,
  StorePickupConfig,
  TimeLimit,
  ZippinConfig,
} from "./types";

let shippingTypesCache: { at: number; data: ShippingTypeOption[] } | null = null;
const SHIPPING_TYPES_TTL = 60_000;

export async function listShippingTypes(force = false): Promise<ShippingTypeOption[]> {
  const now = Date.now();
  if (!force && shippingTypesCache && now - shippingTypesCache.at < SHIPPING_TYPES_TTL) {
    return shippingTypesCache.data;
  }
  const { data } = await axiosInstance.get<ShippingTypeOption[]>(`${API_BASE_URL}/shipping-types`);
  shippingTypesCache = { at: now, data };
  return data;
}

const sellerBase = (sellerCode: string) =>
  `${API_BASE_URL}/sellers/${encodeURIComponent(sellerCode)}/shipping`;

export async function listShippingRates(sellerCode: string): Promise<ShippingRate[]> {
  const { data } = await axiosInstance.get<ShippingRate[]>(`${sellerBase(sellerCode)}/rates`);
  return data;
}

export async function getShippingRate(sellerCode: string, rateId: number): Promise<ShippingRate> {
  const { data } = await axiosInstance.get<ShippingRate>(
    `${sellerBase(sellerCode)}/rates/${rateId}`,
  );
  return data;
}

export async function createShippingRate(
  sellerCode: string,
  payload: Omit<ShippingRate, "id" | "vendorId">,
): Promise<ShippingRate> {
  const { data } = await axiosInstance.post<ShippingRate>(
    `${sellerBase(sellerCode)}/rates`,
    payload,
  );
  return data;
}

export async function updateShippingRate(
  sellerCode: string,
  rateId: number,
  payload: Partial<Omit<ShippingRate, "id" | "vendorId">>,
): Promise<ShippingRate> {
  const { data } = await axiosInstance.patch<ShippingRate>(
    `${sellerBase(sellerCode)}/rates/${rateId}`,
    payload,
  );
  return data;
}

export async function deleteShippingRate(sellerCode: string, rateId: number): Promise<void> {
  await axiosInstance.delete(`${sellerBase(sellerCode)}/rates/${rateId}`);
}

export async function getDeliveryConfig(sellerCode: string): Promise<DeliveryConfig> {
  const { data } = await axiosInstance.get<DeliveryConfig>(
    `${sellerBase(sellerCode)}/delivery-config`,
  );
  return data;
}

export async function updateDeliveryConfig(
  sellerCode: string,
  payload: { timeLimits?: TimeLimit[]; weekOffdays?: number[] },
): Promise<DeliveryConfig> {
  const { data } = await axiosInstance.patch<DeliveryConfig>(
    `${sellerBase(sellerCode)}/delivery-config`,
    payload,
  );
  return data;
}

export async function getStorePickup(sellerCode: string): Promise<StorePickupConfig> {
  const { data } = await axiosInstance.get<StorePickupConfig>(
    `${sellerBase(sellerCode)}/methods/store-pickup`,
  );
  return data;
}

export async function upsertStorePickup(
  sellerCode: string,
  payload: Omit<StorePickupConfig, never>,
): Promise<StorePickupConfig> {
  const { data } = await axiosInstance.put<StorePickupConfig>(
    `${sellerBase(sellerCode)}/methods/store-pickup`,
    payload,
  );
  return data;
}

export async function getZippin(sellerCode: string): Promise<ZippinConfig> {
  const { data } = await axiosInstance.get<ZippinConfig>(
    `${sellerBase(sellerCode)}/methods/zippin`,
  );
  return data;
}

export async function upsertZippin(
  sellerCode: string,
  payload: Omit<ZippinConfig, never>,
): Promise<ZippinConfig> {
  const { data } = await axiosInstance.put<ZippinConfig>(
    `${sellerBase(sellerCode)}/methods/zippin`,
    payload,
  );
  return data;
}
